import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RecentHistory from "./CommonJobBoardTabs/RecentHistory";
import SavedHistory from "./CommonJobBoardTabs/SavedHistory";
import {
  getIntegratedSearchStart,
  resetIntegratedSearch,
} from "../../../redux/slices/internalDbES/internalDbESlice";
import {
  AccordianNew,
  Backdrop,
  Button,
  FormFieldSet,
  LogoLoader,
  NewTab,
  showToast,
  useFormHandler,
} from "../../../components";
import {
  careerBuilderEmploymentClassification,
  careerBuilderRadius,
  careerLevelMonster,
  carrerbuilderapplyLastActivity,
  carrerbuilderJobCategories,
  carrerbuilderkilometers,
  carrerbuildersearchWithMostRecentCompany,
  compensationTypeOptionsCareerBuilder,
  employmentTypeOptionsCareerBuilder,
  excludeResumesWithNoSalaryOptionsCareerBuilder,
  freshnessInDaysOptionsCareerBuilder,
  languagesSpokenOptionsCareerBuilder,
  lastUpdatedOptionsIntegrateSearch,
  lemmatizeOptionsCareerBuilder,
  militeryexperienceOptionsCareerBuilder,
  minimumDegreeOptionsCareerBuilder,
  MonsterEducationLevel,
  monsterTwlidOptions,
  monsterWaOptions,
  optionsMonsterTo,
  radiusOptionsMonsterIntegrateSearch,
  RelocationfilterOptionsCareerBuilder,
  resumeModifiedOptionsCB,
  resumeModifiedToOptionsCB,
  statesArrayOptionMonster,
  workAuthorizationIntegrationSearch,
  workStatusOptionsCB,
  yearsOfExperienceOptionsCB,
} from "../../../assets/data/enumData";
import IntegratedSearchResults from "./IntegratedSearchResults";
import { useLoginUser } from "../../../hooks/useLoginUser";
import { setcareerBuilderAccessidStart } from "../../../redux/slices/internalDbES/internalDbESlice";
import { setMonsterAccessidStart } from "../../../redux/slices/internalDbES/internalDbESlice";
import {
  getCountries,
  getLocations,
} from "../../../redux/slices/internalDbES/internalDbESApi";
import { saveIntegratedRecentSearch } from "../../../redux/slices/bdmjobs/bdmjobsApi";
import { normalizeQuotes } from "../../../utils/common";
import { set } from "lodash";

const buildBooleanQuery = ({ include = "", exclude = "" }) => {
  include = include || ""; // ensure string
  exclude = exclude || "";

  const process = (text) => {
    text = String(text); // force convert to string

    const quoted = [];

    // Extract quoted phrases
    text = text.replace(/"([^"]+)"/g, (_, phrase) => {
      const token = `__Q${quoted.length}__`;
      quoted.push(`"${phrase}"`);
      return ` ${token} `;
    });

    // Tokenize
    const rawTokens = text
      .replace(/([\(\)])/g, " $1 ")
      .split(/\s+/)
      .filter(Boolean);

    // Normalize tokens
    const tokens = rawTokens.map((t) => {
      const up = t.toUpperCase();
      if (["AND", "OR", "NOT"].includes(up)) return up;
      if (t === "(" || t === ")") return t;

      if (/__Q\d+__/.test(t)) {
        const idx = Number(t.match(/\d+/)[0]);
        return quoted[idx];
      }

      return `"${t}"`;
    });

    // Insert implicit AND
    const final = [];
    for (let i = 0; i < tokens.length; i++) {
      const curr = tokens[i];
      const prev = final[final.length - 1];

      const needImplicitAnd =
        prev &&
        curr &&
        prev !== "(" &&
        curr !== ")" &&
        !["AND", "OR", "NOT"].includes(prev) &&
        !["AND", "OR", "NOT"].includes(curr);

      if (needImplicitAnd) final.push("AND");

      final.push(curr);
    }

    return final.join(" ");
  };

  // Build include AND exclude
  const inc = process(include);
  const exc = process(exclude).replace(/"([^"]+)"/g, 'NOT "$1"');

  if (inc && exc) return `${inc} AND ${exc}`;
  if (inc) return inc;
  if (exc) return exc;
  return "";
};

const buildCBBooleanSearch = ({ include = "", exclude = "" }) => {
  const normalize = (str = "") =>
    str
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'")
      .trim()
      .replace(/\s+/g, " ")
      .replace(/\bAND\b/gi, "AND")
      .replace(/\bOR\b/gi, "OR")
      .replace(/\bNOT\b/gi, "NOT");

  const isOperator = (t) => ["AND", "OR", "NOT"].includes(t);
  const isParen = (t) => t === "(" || t === ")";
  const isQuoted = (t) => t.startsWith('"') && t.endsWith('"');
  const isTerm = (t) => !isOperator(t) && !isParen(t);

  const tokenize = (str) =>
    str.match(/"[^"]*"|\(|\)|\bAND\b|\bOR\b|\bNOT\b|[^\s()]+/gi) || [];
  const insertMissingOR = (tokens) => {
    const out = [];

    for (let i = 0; i < tokens.length; i++) {
      const curr = tokens[i];
      const next = tokens[i + 1];

      out.push(curr);
      if (!next) continue;

      if (isQuoted(curr) || isQuoted(next)) continue;

      if (isTerm(curr) && isTerm(next)) {
        out.push("OR");
        continue;
      }

      if (curr === ")" && isTerm(next)) {
        out.push("OR");
        continue;
      }

      if (isTerm(curr) && next === "(") {
        out.push("OR");
        continue;
      }

      if (curr === ")" && next === "(") {
        out.push("OR");
      }
    }

    return out;
  };

  const inc = normalize(include);
  let incTokens = tokenize(inc);
  incTokens = insertMissingOR(incTokens);

  const exc = normalize(exclude);
  let excTokens = tokenize(exc);

  if (excTokens.length) {
    excTokens = excTokens.map((t) =>
      isOperator(t) || isParen(t) ? t : `NOT ${t}`,
    );
  }

  const parts = [];
  if (incTokens.length) parts.push(incTokens.join(" "));
  if (excTokens.length) parts.push(excTokens.join(" AND "));

  return parts.join(" AND ");
};

const buildMBooleanSearch = ({ include = "" }) => {
  const clean = (text) =>
    text
      .split(/[\s,]+/) // split by any space or comma
      .map((t) => t.trim())
      .filter(Boolean);
  // .filter((t) => !["and", "or", "not"].includes(t.toLowerCase())); // remove logical words if typed manually

  const includeKeywords = clean(include);
  const includePart = includeKeywords.join(" ");

  return includePart || "";
};

const parseLocation = (value = "") => {
  const parts = value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

  let city = "";
  let state = "";
  let zipCode = "";

  if (parts.length === 3) {
    // city,state,zip
    [city, state, zipCode] = parts;
  } else if (parts.length === 1) {
    // single value → ONLY state
    state = parts[0];
  }

  return { city, state, zipCode };
};

// const cleanData = (data, prefix = "", res = {}) => {
//   const isEmptyObject = (obj) =>
//     typeof obj === "object" && obj !== null && Object.keys(obj).length === 0;

//   const isPrimitive = (v) =>
//     v === null ||
//     v === undefined ||
//     typeof v === "string" ||
//     typeof v === "number" ||
//     typeof v === "boolean";
//   if (data === "" || data === null || data === undefined) return res;
//   if (Array.isArray(data)) {
//     data.forEach((item, idx) => {
//       const newPrefix = prefix ? `${prefix}.${idx}` : `${idx}`;
//       cleanData(item, newPrefix, res);
//     });
//     return res;
//   }
//   if (typeof data === "object") {
//     if (
//       Object.prototype.hasOwnProperty.call(data, "value") &&
//       (Object.keys(data).length === 1 ||
//         (Object.keys(data).length === 2 &&
//           Object.prototype.hasOwnProperty.call(data, "label")))
//     ) {
//       res[prefix] = data.value;
//       return res;
//     }
//     Object.keys(data).forEach((key) => {
//       const value = data[key];
//       if (
//         value === "" ||
//         value === null ||
//         value === undefined ||
//         isEmptyObject(value)
//       )
//         return;

//       const newPrefix = prefix ? `${prefix}.${key}` : key;
//       cleanData(value, newPrefix, res);
//     });
//     return res;
//   }
//   res[prefix] = data;
//   return res;
// };

const cleanData = (data) => {
  const isEmptyObject = (obj) =>
    typeof obj === "object" &&
    obj !== null &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0;

  // remove empty primitives
  if (data === "" || data === null || data === undefined) {
    return undefined;
  }

  // unwrap { value, label? }
  if (
    typeof data === "object" &&
    data !== null &&
    !Array.isArray(data) &&
    Object.prototype.hasOwnProperty.call(data, "value") &&
    (Object.keys(data).length === 1 ||
      (Object.keys(data).length === 2 &&
        Object.prototype.hasOwnProperty.call(data, "label")))
  ) {
    return data.value;
  }

  // handle arrays
  if (Array.isArray(data)) {
    const cleanedArray = data
      .map(cleanData)
      .filter((item) => item !== undefined);

    return cleanedArray.length ? cleanedArray : undefined;
  }

  // handle objects
  if (typeof data === "object") {
    const cleanedObject = {};

    Object.keys(data).forEach((key) => {
      const cleanedValue = cleanData(data[key]);

      if (cleanedValue !== undefined && !isEmptyObject(cleanedValue)) {
        cleanedObject[key] = cleanedValue;
      }
    });

    return Object.keys(cleanedObject).length ? cleanedObject : undefined;
  }

  // primitive value
  return data;
};

function clearDeep(value) {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return [];
  }
  if (value instanceof Date) {
    return null;
  }
  if (typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = clearDeep(value[key]);
      return acc;
    }, {});
  }
  switch (typeof value) {
    case "string":
      return "";
    case "number":
      return 0;
    case "boolean":
      return false;
    default:
      return value;
  }
}

const areAllFieldsMissing = (obj) => {
  return Object.values(obj).every(
    (val) => val === undefined || val === null || val === "",
  );
};

const IntegratedSearch = () => {
  const { user } = useSelector((state) => state.auth);
  const [searchApplicant, setSearchApplicant] = useState(false);
  const [locationTypingValue, setLocationTypingValue] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState({});
  const { loading } = useSelector((state) => state.internalDbES);
  const { loginUserUiid } = useLoginUser();
  const [isSelection, setIsSelection] = useState(false);

  // Search queries state
  const [iDBQuery, setIDBQuery] = useState(null);
  const [cBQuery, setCBQuery] = useState(null);
  const [monsterQuery, setMonsterQuery] = useState(null);

  // access IDS
  const [cbAccessId, setCbAccessId] = useState(null);
  const [mAccessId, setMAccessId] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  const formatMultiSelect = (value) => {
    return Array.isArray(value) ? value.join(",") : value || "";
  };

  const parseMultiSelect = (value) => {
    if (!value) return [];
    return typeof value === "string" ? value.split(",") : value;
  };



  const { accessData } = useSelector((state) => state.internalDbES);
  const dispatch = useDispatch();

  const cBFields = {
    SearchPattern: "Boo",
    Names: "",
    Keywords: "",
    City: "",
    State: "",
    ZipCode: "",
    Country: "",
    JobTitle: "",
    Company: "",
    SearchRadiusInMiles: "",
    Radius: 50,
    RelocationFilter: "",
    JobCategories: "",
    EmploymentType: "",
    MinimumSalary: "",
    MaximumSalary: "",
    FreshnessInDays: "",
    ExcludeResumesWithNoSalary: "",
    LanguagesSpoken: "",
    CurrentlyEmployed: "",
    ManagementExperience: "",
    MinimumEmployeesManaged: "",
    SecurityClearance: "",
    ExcludeIVRResumes: "",
    MilitaryExperience: "",
    SearchWithMostRecentJobTitle: "",
    SearchWithMostRecentCompany: "",
    Lemmatize: "",
    SearchRadiusInKilometer: "",
    MinimumTravelRequirement: "",
    MinimumDegree: "",
    CompensationType: "",
    MaximumCommute: "",
    ApplyLastActivity: "",
    ExcludeSkills: "",
    WorkStatus: "",
    CBMinimumExperience: "",
    CBMaximumExperience: "",
    ResumeModifiedFrom: "",
    ResumeModifiedTo: "",
    EmploymentClassification: "",
  };
  const iDBFields = {
    keywords: "",
    // searchRadiusWithinState: false,
    // excludeBlocklistedApplicants: false,
    // includeApplicantsWithoutCountry: false,
    location: {
      country: selectedCountryId?.name,
      state: [],
      city: [],
      zipCode: "",
    },
    exp: {
      year: "",
      months: "",
    },
    educationalDetails: "",
    // workAuth: "",
    workAuth: [],
    relocation: {
      willingness: null,
    },
    radius: 50,
    fromDate: "",
    toDate: "",
  };
  const mFields = {
    qrjt: "",
    q: "",
    rpcr: "", // Authorized to work
    country: "United States",
    state: [],
    selectedStates: [""],
    zipcode: "",
    radius: 50,
    edulv: "", // Bachelor's
    qsn: "", // Most recent employer
    qrjd: "", // School attended
    tnsalmin: "",
    tnsalmax: "",
    clv: "", // Mid-level
    tjtp: [], // Employee + Contract
    tjtft: "", // Full-Time
    tjtpt: "", // Not Part-Time
    tjtpd: "", // Not Per Diem
    mdateminage: 0,
    mdatemaxage: 43200, // Resume posted within 30 days
    sort: "rank,distance,mdate",
    selectWithRadius: "Search ZIP Code Radius",
    twlid: "",
    wa: "",
    tsni: 1,
    tsaltyp: "1",
  };

  const commonFields = {
    keyword: "",
    recentJobTitle: "1",
    jobTitle: "",
    country: "United States",
    location: "",
    radius: "50",
    lastUpdated: "",
    applicantsSelection: "",
  };

  const initialFormData = {
    ...commonFields,
    iDB: { ...iDBFields },
    careerBuilder: { ...cBFields },
    monster: { ...mFields },
  };

  let { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialFormData, {
      keyword: { type: "text", required: true },
    });
  const commonFieldsData = {
    keyword: formData?.keyword || selectedTitle,
    recentJobTitle: formData?.recentJobTitle || "",
    jobTitle: formData?.jobTitle || "",
    country: formData?.country || "",
    location: locationTypingValue || "",
    radius: formData?.radius || "",
    lastUpdated: formData?.lastUpdated || "",
    applicantsSelection: formData?.applicantsSelection || "",
  };
  const recentTitles = formData?.recentJobTitle.includes(1)

  const handleSelect = ({ title, filterTerms = {}, recentTitles }) => {
    const internalDb = filterTerms?.internalDB ?? {};
    const careerBuilder = filterTerms?.careerBuilder ?? {};
    const monster = filterTerms?.monster ?? {};
    const commonFields = filterTerms?.commonFields ?? {};
    if (commonFields?.location) {
      setLocationTypingValue("");
      setTimeout(() => {
        setLocationTypingValue(commonFields.location);
      }, 100);
    }
    let city = "";
    let state = "";
    let zipCode = "";
    if (commonFields?.location) {
      const parsedLocation = parseLocation(commonFields?.location);
      city = parsedLocation.city;
      state = parsedLocation.state;
      zipCode = parsedLocation.zipCode;
    }
    const cleanedHistory = internalDb;
    let commonFiledLocationFromIdb = ""
    if (careerBuilder.City && careerBuilder.State && careerBuilder.ZipCode) { commonFiledLocationFromIdb = `${careerBuilder.City},${careerBuilder.State},${careerBuilder.ZipCode}` }
    else if (!careerBuilder.City && careerBuilder.State && !careerBuilder.ZipCode) { commonFiledLocationFromIdb = `${careerBuilder.State}` }
    else { commonFiledLocationFromIdb = "" }
    const commonData = {
      keyword: commonFields?.Keywords ?? "",
      recentJobTitle: commonFields?.recentJobTitle || (cleanedHistory?.jobTitle ? [1] : ""),
      jobTitle: commonFields?.jobTitle || cleanedHistory?.jobTitle || "",
      country: commonFields?.country || careerBuilder?.Country || "",
      location: locationTypingValue || commonFiledLocationFromIdb || "",
      radius: commonFields?.radius || cleanedHistory?.radius || "",
      lastUpdated: commonFields?.lastUpdated || (careerBuilder?.FreshnessInDays ? Number(careerBuilder.FreshnessInDays) == 1095 ? 999 : Number(careerBuilder.FreshnessInDays) : ""),
      applicantsSelection: commonFields?.applicantsSelection ?? "",
    };
    setLocationTypingValue(commonFields?.location || commonFiledLocationFromIdb);
    const idbData = {
      keywords: "",
      applicantsSelection: cleanedHistory["applicantsSelection"] && cleanedHistory["applicantsSelection"],
      //jobTitle: recentTitles ? commonFields?.jobTitle : "",

      location: {
        country:
          cleanedHistory["location.country"] ??
          cleanedHistory?.location?.country ??
          "United States",
        state:
          state && state == cleanedHistory?.location?.state
            ? []
            : cleanedHistory?.location?.state ?? [],

        city:
          city && city == cleanedHistory?.location?.city
            ? ""
            : cleanedHistory?.location?.city ?? "",

        zipCode:
          zipCode && zipCode == cleanedHistory?.location?.zipCode
            ? ""
            : cleanedHistory?.location?.zipCode ?? "",
      },

      exp: {
        year: cleanedHistory["exp.year"] ?? cleanedHistory?.exp?.year ?? "",
        months:
          cleanedHistory["exp.months"] ?? cleanedHistory?.exp?.months ?? "",
      },
      educationalDetails: cleanedHistory["educationalDetails"] ?? "",
      workAuth: cleanedHistory["workAuth"] ?? "",
      relocation: {
        willingness:
          (cleanedHistory["relocation.willingness"] &&
            cleanedHistory["relocation.willingness"]) ??
          null,
      },
      radius: careerBuilder?.Radius ?? 50,
    };

    const cBFieldsData = {
      Names: careerBuilder?.Names ?? "",
      Keywords: careerBuilder?.Keywords ?? "",
      SearchPattern: careerBuilder?.SearchPattern ?? "Boo",
      City: careerBuilder?.City ?? "",
      State: careerBuilder?.State ?? "",
      ZipCode: careerBuilder?.ZipCode ?? "",
      Country: careerBuilder?.Country ?? "US",
      Radius: careerBuilder?.Radius ?? 50,
      //JobTitle: "",
      Company: careerBuilder?.Company ?? "",
      SearchRadiusInMiles:
        careerBuilder?.SearchRadiusInMiles &&
        careerBuilder?.SearchRadiusInMiles,
      RelocationFilter: careerBuilder?.RelocationFilter ?? "",
      JobCategories: careerBuilder?.JobCategories ?? "",
      EmploymentType: careerBuilder?.EmploymentType ?? "",
      MinimumSalary: careerBuilder?.MinimumSalary ?? "",
      MaximumSalary: careerBuilder?.MaximumSalary ?? "",
      FreshnessInDays: careerBuilder?.FreshnessInDays ?? "",
      ExcludeResumesWithNoSalary:
        careerBuilder?.ExcludeResumesWithNoSalary ?? "",
      LanguagesSpoken: careerBuilder?.LanguagesSpoken ?? "",
      CurrentlyEmployed: careerBuilder?.CurrentlyEmployed ?? "",
      ManagementExperience: careerBuilder?.ManagementExperience ?? "",
      MinimumEmployeesManaged: careerBuilder?.MinimumEmployeesManaged ?? "",
      SecurityClearance: careerBuilder?.SecurityClearance ?? "",
      ExcludeIVRResumes: careerBuilder?.ExcludeIVRResumes ?? "",
      MilitaryExperience: careerBuilder?.MilitaryExperience ?? "",
      SearchWithMostRecentJobTitle:
        careerBuilder?.SearchWithMostRecentJobTitle ?? "",
      SearchWithMostRecentCompany:
        careerBuilder?.SearchWithMostRecentCompany ?? "",
      Lemmatize: careerBuilder?.Lemmatize ?? "",
      SearchRadiusInKilometer:
        careerBuilder?.SearchRadiusInKilometer &&
        careerBuilder?.SearchRadiusInKilometer,
      MinimumTravelRequirement: careerBuilder?.MinimumTravelRequirement ?? "",
      MinimumDegree: careerBuilder?.MinimumDegree ?? "",
      CompensationType: careerBuilder?.CompensationType ?? "",
      MaximumCommute: careerBuilder?.MaximumCommute ?? "",
      ApplyLastActivity: careerBuilder?.ApplyLastActivity ?? "",
      ExcludeSkills: careerBuilder?.ExcludeSkills ?? "",
      WorkStatus: parseMultiSelect(careerBuilder?.WorkStatus) ?? "",
      CBMinimumExperience: careerBuilder?.CBMinimumExperience ?? "",
      CBMaximumExperience: careerBuilder?.CBMaximumExperience ?? "",
      ResumeModifiedFrom: careerBuilder?.ResumeModifiedFrom ?? "",
      ResumeModifiedTo: careerBuilder?.ResumeModifiedTo ?? "",
      EmploymentClassification: careerBuilder?.EmploymentClassification ?? "",
    };

    const mFieldsData = {
      q: monster?.q ?? "",
      rpcr: monster?.rpcr ?? "",
      country: monster?.country == "840" ? "United States" : monster?.country == "124" ? "Canada" : "",
      state: monster?.state ?? [],
      selectedStates: monster?.selectedStates ?? [""],
      zipcode: monster?.zipcode ?? "",
      radius: monster?.radius ?? "",
      edulv: monster?.edulv ?? "", // Bachelor's
      qsn: monster?.qsn ?? "", // Most recent employer
      qrjd: monster?.qrjd ?? "", // School attended
      tnsalmin: monster?.tnsalmin && monster?.tnsalmin,
      tnsalmax: monster?.tnsalmax && monster?.tnsalmax,
      clv: monster?.clv ?? "", // Mid-level
      tjtp: monster?.tjtp, // Employee + Contract
      tjtft: monster?.tjtft, // Full-Time
      tjtpt: monster?.tjtpt, // Not Part-Time
      tjtpd: monster?.tjtpd, // Not Per Diem
      mdatemaxage: monster?.mdatemaxage ?? 43200, // Resume posted within 30 days
      sort: monster?.sort ?? "rank,distance,mdate",
      selectWithRadius: monster?.selectWithRadius ?? "Search ZIP Code Radius",
      twlid: monster?.twlid ?? "",
      wa: monster?.wa ?? "",
      tsni: monster?.tsni ?? "",
      //qrjt: "",
      tsaltyp: monster?.tsaltyp ?? "",
      rlid: Array.isArray(monster?.rlid)
        ? [monster?.rlid]
        : typeof monster?.rlid === "number"
          ? [monster?.rlid]   // wrap number into array
          : typeof monster?.rlid === "string" && monster.rlid.length > 0
            ? monster?.rlid.split("+").map(Number)
            : [],
    };

    const initialFormData = {
      ...commonData,
      iDB: { ...idbData },
      careerBuilder: { ...cBFieldsData },
      monster: { ...mFieldsData },
    };
    setFormData(initialFormData);
    setSelectedTitle(title);
  };

  const onSuccess = () => {
    setFormData(initialFormData);
    setLocationTypingValue("");
    setSelectedTitle("");
    //if (
    //setSearchApplicant(true);
    //) return;
    //setSearchApplicant((prev) => !prev);
  };

  const togggleSearchApplicant = () => {
    setSearchApplicant((prev) => !prev);
    dispatch(resetIntegratedSearch());
    clearDeep(formData);
  };

  const populatedJobBoards = () => {
    const {
      iDB,
      careerBuilder,
      monster,
      jobTitle,
      country,
      lastUpdated,
      recentJobTitle,
    } = formData;

    const isEmpty = (v) =>
      v === null ||
      v === undefined ||
      v === "" ||
      (Array.isArray(v) && v.length === 0);

    if (isEmpty(recentJobTitle)) {
      handleChange("careerBuilder.jobTitle", "");
      handleChange("monster.qrjt", "");
      handleChange("iDB.jobTitle", "");
    }

    // Location
    if (monster && isEmpty(monster.country) && country)
      handleChange("monster.country", country);

    if (iDB && isEmpty(iDB.location?.country) && country) {
      handleChange("iDB.location.country", country);
    }
  };

  const formatIntegratedSearchPayload = (filterTerms) => {
    const cbKeywords = buildCBBooleanSearch({
      include: formData?.keyword || selectedTitle,
      exclude: formData?.careerBuilder?.ExcludeSkills,
    });

    const idbKeywords = buildBooleanQuery({
      include: formData?.keyword || selectedTitle,
      exclude: formData?.careerBuilder?.ExcludeSkills,
    });
    // const idbKeywords = formData?.keyword || selectedTitle;

    const mKeywords = buildMBooleanSearch({
      include: formData?.keyword || selectedTitle,
    });

    // function mergeInnerObjects(objLike) {
    //   if (!objLike) return {};
    //   // If it's already an array, take values; otherwise take Object.values for objects with numeric keys
    //   const arr = Array.isArray(objLike) ? objLike : Object.values(objLike);
    //   return arr
    //     .filter(Boolean) // drop null/undefined
    //     .reduce((acc, cur) => ({ ...acc, ...cur }), {});
    // }

    // // Usage
    // const cleanedHistory = mergeInnerObjects(filterTerms?.internalDB ?? {});

    // Add Fields to all


    // Data Clean, Send only added fields
    const cleanedIDB = cleanData(formData?.iDB);
    const cleanedCareerBuilder = cleanData(formData?.careerBuilder);
    const cleanedMonster = cleanData(formData?.monster);

    let city = "";
    let state = "";
    let zipCode = "";
    let mState = ""
    if (locationTypingValue) {
      const parsedLocation = parseLocation(locationTypingValue);
      city = parsedLocation.city;
      state = parsedLocation.state;
      zipCode = parsedLocation.zipCode;
    }
    const radius = formData?.monster?.radius ?? "";
    if (state) {
      mState = statesArrayOptionMonster.find(
        (e) => e.label === state,
      )?.value;
    }
    const internalDB = {
      payload: {
        query: {
          ...cleanedIDB,
          ...(filterTerms?.internalDB ?? {}),
          ...(formData?.iDB?.location?.state?.length > 0 && {
            location: {
              country: formData?.iDB?.location?.country ?? "",
              state: formData?.iDB?.location?.state ?? "",
              city: formData?.iDB?.location?.city ?? "",
            },
          }),
          keywords: idbKeywords,
          ...(locationTypingValue && formData?.iDB?.location?.state.length === 0 && {
            location: {
              country: formData?.iDB?.location?.country ?? "", ...(state && { state }),
              ...(city && { city }),
              ...(zipCode && { zipCode }),
            },

          }),
          ...(formData?.recentJobTitle.includes(1) && { jobTitle: formData?.jobTitle }),

        },
        lim: 25,
        page: 1,
        addedBy: user?.data?.uiid,
        source: 6,
      },
    };
    const cbFilters = filterTerms?.careerBuilder ?? {};
    const careerBuilder = {
      accessId: cbAccessId || accessData?.careerBuilder?.[0]?.uiid,
      userId: user?.data?.uiid,
      payload: {
        query: {
          ...cleanedCareerBuilder,
          ...cbFilters,
          WorkStatus: formatMultiSelect(cleanedCareerBuilder?.WorkStatus ?? cbFilters?.WorkStatus),
          Keywords: cbKeywords,
          ...(formData?.recentJobTitle.includes(1) && { JobTitle: formData?.jobTitle }),
        },
        lim: 25,
        page: 1,
        addedBy: user?.data?.uiid,
        source: 6,
      },
    };
    const monster = {
      accessId: mAccessId || accessData?.monster?.[0]?.uiid,
      searchType: "classic",
      payload: {
        query: {
          ...cleanedMonster,
          country: cleanedMonster?.rpcr && (!formData?.monster?.rlid || formData.monster.rlid.length == 0)
            ? "" // no canada states in integrated monster country dropdown thats why they emptying it(same in old ats directly sending zipcode)
            : cleanedMonster?.country === "United States"
              ? 840
              : cleanedMonster?.country === "Canada"
                ? 124
                : cleanedMonster?.country,
          ...(filterTerms?.monster ?? {}),
          q: mKeywords,
          // qrjt: cleanedMonster?.jt || "",
          mdateminage: 0,
          ...(formData?.recentJobTitle.includes(1) && { qrjt: formData?.jobTitle }),
          rpcr: (Array.isArray(formData?.monster?.rlid) && formData?.monster?.rlid.length > 0
            ? ""
            : (formData?.monster?.rpcr || filterTerms?.monster?.rpcr || "")
          ),
          ...(formData?.monster?.rlid?.length > 0 && {
            rlid: formData?.monster?.rlid.join("+"),
          }),
          ...(formData?.monster?.rlid?.length > 0 && {
            zipcode: "",
          }),
          ...(city && state && zipCode && formData?.monster?.rlid?.length == 0 && {
            rpcr: `${zipCode}-${radius}`,
            zipcode: `${zipCode}`,
            rlid: "",
          }),
          ...((!formData?.monster?.rlid || formData?.monster?.rlid.length == 0) && !city && state && !zipCode && {
            rlid: Number(mState),
          }),
          ...((typeof formData?.monster?.rlid === "number") && !city && state && !zipCode && {
            rlid: Number(mState),
          }),
          ...(city && state && zipCode && (typeof formData?.monster?.rlid === "number") && {
            rpcr: `${zipCode}-${radius}`,
            zipcode: `${zipCode}`,
            rlid: "",
          }),
        },
        lim: 25,
        page: 1,
        addedBy: user?.data?.uiid,
        source: 6,
      },
    };

    // Set Query states
    setIDBQuery(internalDB);
    setCBQuery(careerBuilder);
    setMonsterQuery(monster);

    return { internalDB, careerBuilder, monster };
  };

  const handleIntegratedSearch = async (title, filterTerms) => {
    //console.log("initaial form data :", initialFormData);
    if (!formData?.keyword && !title && !selectedTitle) {
      showToast("Keywords required.");
      return;
    }
    const keyword = formData?.keyword || title || selectedTitle;
    const openParen = (keyword.match(/\(/g) || []).length;
    const closeParen = (keyword.match(/\)/g) || []).length;

    if (openParen !== closeParen) {
      showToast("Invalid keyword syntax: missing ')'.", "error", 2000);
      return;
    }

    // check unbalanced double quotes
    const quotes = (keyword.match(/"/g) || []).length;

    if (quotes % 2 !== 0) {
      showToast('Invalid keyword syntax: missing closing "', "error", 2000);
      return;
    }

    const { internalDB, careerBuilder, monster } =
      formatIntegratedSearchPayload(filterTerms);
    populatedJobBoards();

    try {
      setSearchApplicant(true);
      dispatch(
        getIntegratedSearchStart({
          internalDB,
          careerBuilder,
          monster,
          onSuccess,
        }),
      );
      const HistorySavePayload = {
        source: 6,
        addedBy: user?.data?.uiid,
        query: {
          commonFields: commonFieldsData,
          internalDB: internalDB.payload.query,
          careerBuilder: careerBuilder.payload.query,
          monster: monster.payload.query,
        },
        page: 1,
        limit: 30,
        status: 1,
      };
      //console.log(HistorySavePayload?.query)
      const saveHistory = await saveIntegratedRecentSearch(HistorySavePayload);
      //   payload
      // )
    } catch (error) {
      console.error("Integrated search failed:", error);
    }
  };

  //console.log(formData)
  const onSearch = async ({ title, filterTerms }) => {
    // console.log(title,filterTerms)
    if (!title) return;

    formData = { ...formData, keyword: `${title}` };
    setFormData(formData);

    //console.log(newForm);
    await handleIntegratedSearch(title, filterTerms);
    setSelectedTitle("");

  };

  const integratedSearchMenus = [
    {
      title: "Recent History",
      page: (
        <RecentHistory
          board="integratedSearch"
          onSelect={handleSelect}
          onClick={onSearch}
        />
      ),
      type: "main",
    },
    {
      title: "Saved History",
      page: (
        <SavedHistory
          board="integratedSearch"
          onSelect={handleSelect}
          onClick={onSearch}
        />
      ),
      type: "main",
    },
  ];




  const monsterData = (
    <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
      <FormFieldSet
        noTitle
        type="checkbox"
        options={monsterTwlidOptions}
        onChange={(value) => handleChange("monster.twlid", value)}
        values={formData?.monster?.twlid}
      />
      <FormFieldSet
        noTitle
        type="checkbox"
        options={monsterWaOptions}
        onChange={(value) => handleChange("monster.wa", value)}
        values={formData?.monster?.wa}
      />
      <div className="grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-3 gap-4">
        <FormFieldSet
          type="select"
          placeholder="Select"
          title="Resume Posted"
          options={optionsMonsterTo}
          onChange={(value) => handleChange("monster.mdatemaxage", value)}
          value={formData.monster?.mdatemaxage}
        />
        <FormFieldSet
          // type="select"
          type="countriesUsAndCanada"
          placeholder="Select"
          title="Country"
          value={formData.monster?.country}
          onChange={(value) => { handleChange("monster.country", value.value); handleChange("monster.rlid", "") }}
        />
        <FormFieldSet
          type="select"
          //type="states"
          multiple={true}
          placeholder="Select"
          title="State"
          options={formData.monster?.country == "Canada" ? [] : statesArrayOptionMonster}
          parentValue={{ country: formData.monster?.country }}
          value={formData.monster?.rlid}
          onChange={(value) => { handleChange("monster.rlid", value) }}
          disabled={!formData.monster?.country}
          showSearch={true}
        />
        <FormFieldSet
          type="select"
          placeholder="Select"
          title="Job Seeker Profile"
          options={MonsterEducationLevel}
          onChange={(value) => handleChange("monster.edulv", value)}
          value={formData.monster?.edulv}
        />
        <FormFieldSet
          placeholder="Most Recent Employer"
          title="Most Recent Employer"
          onChange={(value) => handleChange("monster.qsn", value)}
          value={formData.monster?.qsn}
        />
        <FormFieldSet
          placeholder="School Attended"
          title="School Attended"
          value={formData.monster?.qrjd}
          onChange={(value) => handleChange("monster.qrjd", value)}
        />
        <div className="grid grid-cols-2 gap-4 col-span-2">
          <FormFieldSet
            type="text"
            title="Desired Salary"
            placeholder="Min(Annually)"
            value={formData.monster?.tnsalmin}
            onChange={(value) => handleChange("monster.tnsalmin", value)}
          />
          <FormFieldSet
            type="text"
            title=" "
            placeholder="Max(Annually)"
            value={formData.monster?.tnsalmax}
            onChange={(value) => handleChange("monster.tnsalmax", value)}
          />
        </div>
        <FormFieldSet
          type="select"
          placeholder="Select"
          title="Career Level"
          options={careerLevelMonster}
          value={formData.monster?.clv}
          onChange={(value) => handleChange("monster.clv", value)}
        />
      </div>
      <div className="grid grid-cols-1 my-1">
        <FormFieldSet
          title="Job Type"
          type="checkbox"
          name={"mJobType"}
          options={[
            { label: "Employee", value: 1 },
            { label: "Intern", value: 2 },
            { label: "Temporary/Contract/Permanent", value: 3 },
            { label: "Seasonal", value: 4 },
          ]}
          multiple
          onChange={(value) => handleChange("monster.tjtp", value)}
          values={formData?.monster?.tjtp}
        />
        <div className=" mt-2">
          <h6 className="text-sm !font-[400]">Job Status</h6>
          <div className="grid xl:grid-cols-1 lg:grid-cols-3">
            <FormFieldSet
              noTitle
              type="checkbox"
              options={[{ label: "Full-Time", value: 1 }]}
              multiple
              onChange={(value) => handleChange("monster.tjtft", value)}
              values={formData?.monster?.tjtft}
            />
            <FormFieldSet
              noTitle
              type="checkbox"
              options={[{ label: "Part-Time", value: 1 }]}
              multiple
              onChange={(value) => handleChange("monster.tjtpt", value)}
              values={formData?.monster?.tjtpt}
            />
            <FormFieldSet
              noTitle
              type="checkbox"
              options={[{ label: "Per Diem", value: 1 }]}
              multiple
              onChange={(value) => handleChange("monster.tjtpd", value)}
              values={formData?.monster?.tjtpd}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const CareerBuilderData = (
    <div>
      {/* <FormFieldSet
        type="checkbox"
        noTitle
        options={[
          {
            label:
              "Include Job Seekers Employable in any of the selected locations",
            value: 1,
          },
          {
            label:
              "Include Job Seekers Authorized to work in any of the selected locations",
            value: 2,
          },
        ]}
      /> */}
      <div className="grid grid-cols-3 2xl:grid-cols-3 lg:grid-cols-2 gap-3 my-3">
        {/* <FormFieldSet
          title="Name"
          value={formData?.careerBuilder?.Names}
          onChange={(value) => handleChange("careerBuilder.Names", value)}
          placeholder="Name"
        /> */}
        <FormFieldSet
          options={carrerbuilderJobCategories}
          value={formData?.careerBuilder?.JobCategories}
          title="Job Category"
          placeholder="Select"
          type="select"
          onChange={(value) =>
            handleChange("careerBuilder.JobCategories", value)
          }
          showSearch
        />
        <FormFieldSet
          title="Relocation Filter"
          placeholder="Select"
          type="select"
          options={RelocationfilterOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.RelocationFilter", value)
          }
          value={formData?.careerBuilder?.RelocationFilter}
        />
        <FormFieldSet
          title="Search Radius in Miles"
          placeholder="Select"
          type="select"
          options={careerBuilderRadius}
          onChange={(value) =>
            handleChange("careerBuilder.SearchRadiusInMiles", value)
          }
          value={formData?.careerBuilder?.SearchRadiusInMiles}
        />
        <FormFieldSet
          title="Freshness In Days"
          placeholder="Select"
          type="select"
          options={freshnessInDaysOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.FreshnessInDays", value)
          }
          value={formData?.careerBuilder?.FreshnessInDays}
        />
        <FormFieldSet
          title="Employment Type"
          placeholder="Select"
          type="select"
          options={employmentTypeOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.EmploymentType", value)
          }
          value={formData?.careerBuilder?.EmploymentType}
        />

        <FormFieldSet
          title="Minimum Degree"
          placeholder="Select"
          type="select"
          options={minimumDegreeOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.MinimumDegree", value)
          }
          value={formData?.careerBuilder?.MinimumDegree}
        />
        <FormFieldSet
          title="Compensation Type"
          placeholder="Select"
          type="select"
          options={compensationTypeOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.CompensationType", value)
          }
          value={formData?.careerBuilder?.CompensationType}
        />
        <FormFieldSet
          title="Minimum Salary"
          value={formData?.careerBuilder?.MinimumSalary}
          placeholder="Enter"
          onChange={(value) =>
            handleChange("careerBuilder.MinimumSalary", value)
          }
        />
        <FormFieldSet
          title="Maximum Salary"
          value={formData?.careerBuilder?.MaximumSalary}
          placeholder="Enter"
          onChange={(value) =>
            handleChange("careerBuilder.MaximumSalary", value)
          }
        />
        <FormFieldSet
          title="Languages Spoken"
          placeholder="Select"
          type="select"
          options={languagesSpokenOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.LanguagesSpoken", value)
          }
          value={formData?.careerBuilder?.LanguagesSpoken}
          showSearch
        />
        <FormFieldSet
          title="Currently Employed"
          placeholder="Select"
          type="select"
          options={excludeResumesWithNoSalaryOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.CurrentlyEmployed", value)
          }
          value={formData?.careerBuilder?.CurrentlyEmployed}
        />
        <FormFieldSet
          title="Management Experience"
          placeholder="Select"
          type="select"
          options={excludeResumesWithNoSalaryOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.ManagementExperience", value)
          }
          value={formData?.careerBuilder?.ManagementExperience}
        />
        <FormFieldSet
          title="Maximum Commute"
          placeholder="Enter"
          value={formData?.careerBuilder?.MaximumCommute}
          onChange={(value) =>
            handleChange("careerBuilder.MaximumCommute", value)
          }
        />
        <FormFieldSet
          title="Exclude Resumes with No Salary"
          placeholder="Select"
          type="select"
          options={excludeResumesWithNoSalaryOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.ExcludeResumesWithNoSalary", value)
          }
          value={formData?.careerBuilder?.ExcludeResumesWithNoSalary}
        />
        <FormFieldSet
          title="Minimum Employees Managed"
          placeholder="Enter"
          value={formData?.careerBuilder?.MinimumEmployeesManaged}
          onChange={(value) =>
            handleChange("careerBuilder.MinimumEmployeesManaged", value)
          }
        />

        <FormFieldSet
          title="Security Clearance"
          placeholder="Select"
          type="select"
          options={excludeResumesWithNoSalaryOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.SecurityClearance", value)
          }
          value={formData?.careerBuilder?.SecurityClearance}
        />
        <FormFieldSet
          title="Exclude VR Resumes"
          placeholder="Select"
          type="select"
          options={excludeResumesWithNoSalaryOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.ExcludeIVRResumes", value)
          }
          value={formData?.careerBuilder?.ExcludeIVRResumes}
        />
        <FormFieldSet
          title="Military Experience"
          placeholder="Select"
          type="select"
          options={militeryexperienceOptionsCareerBuilder}
          onChange={(value) =>
            handleChange("careerBuilder.MilitaryExperience", value)
          }
          value={formData?.careerBuilder?.MilitaryExperience}
        />
        <FormFieldSet
          title="Search Radius in Kilometer"
          placeholder="Select"
          type="select"
          options={carrerbuilderkilometers}
          onChange={(value) =>
            handleChange("careerBuilder.SearchRadiusInKilometer", value)
          }
          value={formData?.careerBuilder?.SearchRadiusInKilometer}
        />
        <FormFieldSet
          title="Lemmatize"
          placeholder="Select"
          type="select"
          options={lemmatizeOptionsCareerBuilder}
          onChange={(value) => handleChange("careerBuilder.Lemmatize", value)}
          value={formData?.careerBuilder?.Lemmatize}
        />
        <FormFieldSet
          title="Apply Last Activity"
          placeholder="Select"
          type="select"
          options={carrerbuilderapplyLastActivity}
          onChange={(value) =>
            handleChange("careerBuilder.ApplyLastActivity", value)
          }
          value={formData?.careerBuilder?.ApplyLastActivity}
        />
        <FormFieldSet
          title="Apply Last ActivitySearch With Most Recent Job Title"
          placeholder="Select"
          type="select"
          options={carrerbuildersearchWithMostRecentCompany}
          onChange={(value) =>
            handleChange("careerBuilder.SearchWithMostRecentJobTitle", value)
          }
          value={formData?.careerBuilder?.SearchWithMostRecentJobTitle}
        />
        <FormFieldSet
          title="Apply Last ActivitySearch With Most Recent Company"
          placeholder="Select"
          type="select"
          options={carrerbuildersearchWithMostRecentCompany}
          onChange={(value) =>
            handleChange("careerBuilder.SearchWithMostRecentCompany", value)
          }
          value={formData?.careerBuilder?.SearchWithMostRecentCompany}
        />
        <FormFieldSet
          title="Exclude Skills"
          placeholder="Enter"
          onChange={(value) =>
            handleChange("careerBuilder.ExcludeSkills", value)
          }
          value={formData?.careerBuilder?.ExcludeSkills}
        />
        <FormFieldSet
          title="Employer"
          placeholder="Enter"
          onChange={(value) => handleChange("careerBuilder.Company", value)}
          value={formData?.careerBuilder?.Company}
        />
        <FormFieldSet
          title="Work Status"
          placeholder="Select"
          type="select"
          multiple
          options={workStatusOptionsCB}
          onChange={(value) => handleChange("careerBuilder.WorkStatus", value)}
          value={formData?.careerBuilder?.WorkStatus}
        />
        {/* <div className="grid grid-cols-1 2xl:grid-cols-2 gap-2 "> */}
        <FormFieldSet
          title="Total Experience"
          placeholder="Min"
          type="select"
          options={yearsOfExperienceOptionsCB}
          onChange={(value) =>
            handleChange("careerBuilder.CBMinimumExperience", value)
          }
          value={formData?.careerBuilder?.CBMinimumExperience}
        />
        <FormFieldSet
          title=""
          placeholder="Max"
          type="select"
          options={yearsOfExperienceOptionsCB}
          onChange={(value) =>
            handleChange("careerBuilder.CBMaximumExperience", value)
          }
          value={formData?.careerBuilder?.CBMaximumExperience}
        />

        <FormFieldSet
          title="Last Modified"
          placeholder="in year"
          type="select"
          options={resumeModifiedOptionsCB}
          onChange={(value) =>
            handleChange("careerBuilder.ResumeModifiedFrom", value)
          }
          value={formData?.careerBuilder?.ResumeModifiedFrom}
        />
        <FormFieldSet
          title=" "
          placeholder="in months"
          type="select"
          options={resumeModifiedToOptionsCB}
          onChange={(value) =>
            handleChange("careerBuilder.ResumeModifiedTo", value)
          }
          value={formData?.careerBuilder?.ResumeModifiedTo}
        />
      </div>
      {/* Duplicate */}
      {/* <div className="col-span-2 2xl:col-span-1">
          <FormFieldSet
            title="Job Type"
            type="checkbox"
            list
            options={[
              { label: "Employee", value: 1 },
              { label: "Intern", value: 2 },
              { label: "Temporary/Contract/Permanant", value: 3 },
            ]}
          />
        </div> */}

      <FormFieldSet
        title="Job Status"
        type="checkbox"
        list
        options={careerBuilderEmploymentClassification}
        onChange={(value) =>
          handleChange("careerBuilder.EmploymentClassification", value)
        }
        values={formData?.careerBuilder?.EmploymentClassification}
        multiple
      />
    </div>
  );

  const data = [
    {
      title: "Internal DB",
      content: (
        <div>
          <div className="grid grid-cols-1  mb-2">
            <FormFieldSet
              type="checkbox"
              title=" "
              options={[
                { label: "Search Radius within the state", value: 1 },
                { label: "Exclude Blocklisted Applicants", value: 2 },
                { label: "Include Applicants without country", value: 3 },
              ]}
              multiple
              values={formData.iDB.applicantsSelection}
              onChange={(value) =>
                handleChange("iDB.applicantsSelection", value)
              }
            />
          </div>
          <div className="grid grid-cols-3 gap-6">
            <FormFieldSet
              type="countriesUsAndCanada"
              placeholder="Select"
              title="Country"
              value={formData.iDB?.location?.country}
              onChange={(value) =>
                handleChange("iDB.location.country", value.value)
              }
            />
            <FormFieldSet
              type="states"
              placeholder="State"
              title="State"
              parentValue={{
                country: formData.iDB?.location?.country,
              }}
              value={formData.iDB?.location?.state}
              onChange={(value) =>
                handleChange("iDB.location.state", value)
              }
              disabled={!formData.iDB?.location?.country}
              multiple={true}
              showSearch={true}
            />
            <FormFieldSet
              type="cities"
              placeholder="City"
              title="City"
              value={formData.iDB?.location?.city}
              onChange={(value) => {
                handleChange("iDB.location.city", value);
              }}
              parentValue={{
                country: formData?.iDB?.location?.country,
                state: formData?.iDB?.location?.state,
              }}
              disabled={formData?.iDB?.location?.state?.length === 0}
              showSearch={true}
              multiple={true}
            />
            <div className="col-span-3">
              <div className="grid grid-cols-2 gap-2 items-end">
                <FormFieldSet
                  type="text"
                  title="Total Years of Experience"
                  placeholder="Years"
                  value={formData?.iDB?.exp?.year}
                  onChange={(value) => handleChange("iDB.exp.year", value)}
                />

                <FormFieldSet
                  type="select"
                  title=" "
                  placeholder="Month"
                  value={formData.iDB?.exp?.months}
                  onChange={(value) => handleChange("iDB.exp.months", value)}
                  options={[
                    { label: "1", value: "1" },
                    { label: "2", value: "2" },
                    { label: "3", value: "3" },
                    { label: "4", value: "4" },
                    { label: "5", value: "5" },
                    { label: "6", value: "6" },
                    { label: "7", value: "7" },
                    { label: "8", value: "8" },
                    { label: "9", value: "9" },
                    { label: "10", value: "10" },
                    { label: "11", value: "11" },
                    { label: "12", value: "12" },
                  ]}
                />
              </div>
            </div>

            <FormFieldSet
              placeholder="Education Details"
              title="Education Details"
              value={formData?.iDB?.educationalDetails}
              onChange={(value) =>
                handleChange("iDB.educationalDetails", value)
              }
            />
            <FormFieldSet
              type="select"
              placeholder="Work Authorization"
              title="Work Authorization"
              value={formData?.iDB?.workAuth}
              onChange={(value) => handleChange("iDB.workAuth", value)}
              options={workAuthorizationIntegrationSearch}
              showSearch={true}
              multiple={true}
            />
            <FormFieldSet
              type="radio"
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              title="Willing to Relocate"
              value={formData.iDB?.relocation?.willingness}
              onChange={(event) =>
                handleChange("iDB.relocation.willingness", event.target.value)
              }
            />
          </div>
        </div>
      ),
    },
    {
      title: "Career Builder",
      content: CareerBuilderData,
    },
    {
      title: "Monster ",
      content: monsterData,
    },
  ];

  useEffect(() => {
    const fetchMonsterIntegrationAccess = async () => {
      try {
        const params = {
          coll: "IntegrationAccess",
          fil: {
            userId: {
              type: "FIND_INTIGRATION_ACCESS",
              value: loginUserUiid,
              category: 3,
              categoryType: 1,
              status: 1,
            },
          },
          useAggregate: true,
        };
        // const response = await getAllIntegrationAccess(params);
        // const status = response?.data?.data?.[0]?.integrationData?.status;
        // const accessId = response?.data?.data?.[0]?.uiid ?? null;
        const dispatchData = () => {
          dispatch(setMonsterAccessidStart(params));
          const { accessData } = useSelector((state) => state.internalDbES);
          return {
            status: accessData?.monster?.[0]?.integrationData?.status ?? null,
            accessId: accessData?.monster?.[0]?.uiid ?? null,
          };
        };
        const status =
          accessData?.monster?.[0]?.integrationData?.status ??
          dispatchData()?.status;
        const accessId =
          accessData?.monster?.[0]?.uiid ?? dispatchData()?.accessId;

        setMAccessId(accessId);
        if (typeof status !== "undefined") {
          if (status === 1) {
          } else {
            showToast(
              "Monster integration access not found. Please contact the administrator",
              "error",
            );
          }
        } else {
          showToast(
            "Monster integration status not found. Please contact the administrator",
            "error",
          );
        }
      } catch (err) {
      } finally {
      }
    };

    const fetchCBIntegrationAccess = async () => {
      try {
        const params = {
          coll: "IntegrationAccess",
          fil: {
            userId: {
              type: "FIND_INTIGRATION_ACCESS",
              value: loginUserUiid,
              category: 3,
              categoryType: 2,
              status: 1,
            },
          },
          useAggregate: true,
        };

        // const response = await getAllIntegrationAccess(params);
        // const status = response?.data?.data?.[0]?.integrationData?.status;
        // const accessId = response?.data?.data?.[0]?.uiid ?? null;

        const dispatchData = () => {
          dispatch(setcareerBuilderAccessidStart(params));
          const { accessData } = useSelector((state) => state.internalDbES);
          return {
            status:
              accessData?.careerBuilder?.[0]?.integrationData?.status ?? null,
            accessId: accessData?.careerBuilder?.[0]?.uiid ?? null,
          };
        };

        const accessId =
          accessData?.careerBuilder?.[0]?.uiid ?? dispatchData()?.accessId;
        const status =
          accessData?.careerBuilder?.[0]?.integrationData?.status ??
          dispatchData()?.status;
        setCbAccessId(accessId);
        if (typeof status !== "undefined") {
          if (status === 1) {
          } else {
            showToast(
              "Career Builder integration access not found. Please contact the administrator",
              "error",
            );
          }
        } else {
          showToast(
            "Career Builder integration status not found. Please contact the administrator",
            "error",
          );
        }
      } catch (err) {
      } finally {
      }
    };

    // Integration Access Functions
    fetchCBIntegrationAccess();
    fetchMonsterIntegrationAccess();
  }, []);

  useEffect(() => {
    // if (!locationTypingValue) {
    //   setLocations([]);
    //   return;
    // }
    if (!locationTypingValue || isSelection) {
      setIsSelection(false);
      return;
    }
    const [city, state, zip] = locationTypingValue.split(",");
    if (city && state) {
      setLocations([]);
      return;
    }

    const delay = setTimeout(async () => {
      setLocations([]);
      const response = await getLocations({
        locationValue: locationTypingValue,
        country: selectedCountryId?._id,
        limit: 500,
      });
      if (response?.success) {
        const uniqueStates = [
          ...new Set(
            (response?.data?.data || []).map((item) => item.stateName),
          ),
        ];
        const locationData = response?.data?.data.flatMap((item) => {
          const name = item.name ?? "";
          const state = item.stateName ?? "";
          if (Array.isArray(item.zipCode)) {
            return item.zipCode.map((zc) => `${name},${state},${zc}`);
          }
          return [`${name},${state},${item.zipCode ?? ""}`];
        });
        const search = locationTypingValue.toLowerCase();
        const statesStartsWith = uniqueStates.filter((item) =>
          item.toLowerCase().startsWith(search),
        );
        const statesContains = uniqueStates.filter(
          (item) =>
            item.toLowerCase().includes(search) &&
            !item.toLowerCase().startsWith(search),
        );
        const locationsStartsWith = locationData.filter((item) =>
          item.toLowerCase().startsWith(search),
        );
        const locationsContains = locationData.filter(
          (item) =>
            item.toLowerCase().includes(search) &&
            !item.toLowerCase().startsWith(search),
        );
        statesStartsWith.sort((a, b) => a.localeCompare(b));
        statesContains.sort((a, b) => a.localeCompare(b));
        locationsStartsWith.sort((a, b) => a.localeCompare(b));
        locationsContains.sort((a, b) => a.localeCompare(b));
        setLocations([
          ...statesStartsWith,
          ...locationsStartsWith,
          ...statesContains,
          ...locationsContains,
        ]);
      }
    }, 0);

    return () => clearTimeout(delay);
  }, [locationTypingValue, formData?.country]);

  useEffect(() => {
    if (!formData?.country) return;

    const delay = setTimeout(async () => {
      const response = await getCountries({ name: formData?.country });
      const countries = response.data.data;
      const desiredCountryId = countries[0];
      setSelectedCountryId(desiredCountryId);
      setLocationTypingValue("");

      handleChange("country", desiredCountryId?.name);
      handleChange("monster.country", desiredCountryId?.name);
      handleChange("iDB.location.country", desiredCountryId?.name);
      handleChange("careerBuilder.Country", desiredCountryId?.iso2);
      handleChange("monster.rlid", "")
    }, 0);

    return () => clearTimeout(delay);
  }, [formData?.country]);

  return !searchApplicant ? (
    <div>
      <div className="p-3 flex flex-1 md:flex-col lg:flex-row shadow inner-section rounded m-3">
        <div className="flex-1">
          <div className="grid grid-cols-1 mt-3">
            <FormFieldSet
              title="Keyword"
              placeholder="Enter Keyword"
              value={formData.keyword || selectedTitle}
              onChange={(value) => {
                // handleChange("keyword", value);
                handleChange("keyword", normalizeQuotes(value));
                setSelectedTitle("");
              }}
              error={errors.keyword}
            />
          </div>
          <div className="grid grid-cols-1 my-3">
            <FormFieldSet
              noTitle
              type="checkbox"
              options={[{ label: "Recent Job Title", value: 1 }]}
              values={formData.recentJobTitle}
              onChange={(value) => { handleChange("recentJobTitle", value) }}
              error={errors.recentJobTitle}
              multiple
            />
          </div>
          <div className="grid grid-cols-3 my-3 gap-5">
            <FormFieldSet
              title="Job Title"
              placeholder="Job Title"
              value={formData.jobTitle}
              onChange={(value) => {
                // handleChange("careerBuilder.JobTitle", value);
                // handleChange("monster.qrjt", value);
                // handleChange("iDB.jobTitle", value);

                handleChange("jobTitle", value);
              }}
              error={errors.jobTitle}
            />
            <FormFieldSet
              type="countriesUsAndCanada"
              title="Country"
              placeholder="Select"
              // type="select"
              // options={[
              //   { label: "United States", value: "US" },
              //   { label: "Canada", value: "CA" },
              // ]}
              value={formData.country}
              onChange={(value) => {
                handleChange("country", value.value);
              }}
            />
            {/* <FormFieldSet
            title="Location"
            placeholder="Enter Location"
            value={formData.location}
            onChange={(value) => handleChange("location", value)}
          // error={errors.city}
          /> */}
            <FormFieldSet
              label="Location"
              title="Location"
              type="text"
              placeholder="Location"
              value={locationTypingValue}
              // onChange={(e) => {
              //   setLocationTypingValue(e)
              //   const [city, state, zipCode] = e.split(",");

              //   if (city && state) {
              //     // iDB
              //     handleChange("iDB.location.zipCode", zipCode || "")
              //     handleChange("iDB.location.state", state || "")
              //     handleChange("iDB.location.city", city || "")

              //     // cb
              //     handleChange("careerBuilder.City", city || "")
              //     handleChange("careerBuilder.State", state || "")
              //     handleChange("careerBuilder.ZipCode", zipCode || "")

              //     // monster
              //     handleChange("monster.zipcode", zipCode || "")
              //     // const mState = statesArrayOptionMonster.find(e => e.label == state)?.value;
              //     // handleChange("monster.rlid", Number(mState) || "")
              //     const radius = formData?.monster?.radius || "";
              //     handleChange("monster.rpcr", zipCode && `${zipCode}-${radius}`);
              //   } else if (city) {
              //     handleChange("iDB.location.state", city || "")

              //     // cb
              //     handleChange("careerBuilder.State", city || "")
              //     handleChange("careerBuilder.City", "")
              //     handleChange("careerBuilder.ZipCode", "")

              //     // monster
              //     handleChange("monster.zipcode", "")
              //     const state = statesArrayOptionMonster.find(e => e.label == city)?.value;
              //     handleChange("monster.rlid", Number(state))
              //     handleChange("monster.rpcr", "");
              //   }
              //   }
              // }
              onChange={(e) => {
                setLocationTypingValue(e);
                setIsSelection(locations.includes(e));
                if (!e || e.trim() === "") {
                  // Clear iDB location
                  handleChange("iDB.location.city", "");
                  handleChange("iDB.location.state", "");
                  handleChange("iDB.location.zipCode", "");

                  // Clear careerBuilder location
                  handleChange("careerBuilder.City", "");
                  handleChange("careerBuilder.State", "");
                  handleChange("careerBuilder.ZipCode", "");

                  // Clear monster location
                  handleChange("monster.zipcode", "");
                  handleChange("monster.rpcr", "");
                  // handleChange("monster.rlid", "");
                  return;
                }
                if (locations.includes(e)) {
                  setLocations([]);
                }

                const { city, state, zipCode } = parseLocation(e);

                if (city && state) {
                  // iDB
                  // handleChange("iDB.location.city", city);
                  // handleChange("iDB.location.state", state);
                  // handleChange("iDB.location.zipCode", zipCode || "");

                  // careerBuilder
                  handleChange("careerBuilder.City", city);
                  handleChange("careerBuilder.State", state);
                  handleChange("careerBuilder.ZipCode", zipCode || "");

                  // monster
                  handleChange("monster.zipcode", zipCode || "");
                  const radius = formData?.monster?.radius || "";
                  handleChange(
                    "monster.rpcr",
                    zipCode ? `${zipCode}-${radius}` : "",
                  );
                } else if (state) {
                  // single value → ONLY state

                  // iDB
                  handleChange("iDB.location.city", "");
                  handleChange("iDB.location.state", "");
                  handleChange("iDB.location.zipCode", "");

                  // careerBuilder
                  handleChange("careerBuilder.City", "");
                  handleChange("careerBuilder.State", state);
                  handleChange("careerBuilder.ZipCode", "");

                  // monster
                  handleChange("monster.zipcode", "");
                  const mState = statesArrayOptionMonster.find(
                    (e) => e.label === state,
                  )?.value;
                  if (!formData?.monster?.rlid || formData.monster.rlid.length == 0) {
                    handleChange("monster.rlid", Number(mState) || "");
                  }
                  handleChange("monster.rpcr", "");
                }
              }}
              suggestionsList={locations}
            />
            {/* {console.log({ locationTypingValue })} */}
            <FormFieldSet
              title="Radius"
              placeholder="Select"
              type="select"
              value={formData.radius}
              options={radiusOptionsMonsterIntegrateSearch}
              // value={formData.city}
              onChange={(value) => {
                const zipCode = formData?.monster?.zipcode || "";

                handleChange("iDB.radius", Number(value));

                // handleChange("monster.rpcr", `${zipCOde}-${value}`);
                handleChange(
                  "monster.rpcr",
                  zipCode ? `${zipCode}-${value}` : "",
                );
                handleChange("radius", value);

                handleChange("careerBuilder.Radius", Number(value));
                handleChange("monster.radius", Number(value));
              }}
            // error={errors.city}
            />
            <FormFieldSet
              title="Last Updated"
              placeholder="Select"
              type="select"
              value={formData.lastUpdated}
              options={lastUpdatedOptionsIntegrateSearch}
              // value={formData.city}
              onChange={(value) => {
                const lastUpdated = Number(value);
                handleChange("lastUpdated", value);
                if (lastUpdated === 999 || !lastUpdated) {
                  handleChange("iDB.fromDate", "");
                  handleChange("iDB.toDate", "");
                } else {
                  const toDate = new Date();
                  const fromDate = new Date();
                  fromDate.setDate(toDate.getDate() - lastUpdated);
                  handleChange("iDB.fromDate", fromDate.toISOString());
                  handleChange("iDB.toDate", toDate.toISOString());
                }
                handleChange(
                  "monster.mdatemaxage",
                  lastUpdated === 999
                    ? "1576800" // ~3 years
                    : lastUpdated
                      ? String(lastUpdated * 1440)
                      : "43200", // default 30 days
                );
                handleChange(
                  "careerBuilder.FreshnessInDays",
                  lastUpdated === 999
                    ? "1095" // ~3 years
                    : lastUpdated
                      ? String(lastUpdated)
                      : "", // default 30 days
                );
              }}
            // error={errors.city}
            />
          </div>
          <div className="grid grid-cols-1 my-5">
            <AccordianNew
              data={data}
              openDefault={null}
              iconPosition="right"
              variant="outlined"
              color={"white"}
              activeColor="primary"
            />
          </div>
          <div className="flex justify-end">
            <div className="flex gap-3 ">
              <Button
                title={"Reset"}
                size="sm"
                variant="outlined"
                onClick={() => {
                  setFormData(initialFormData);
                  setSelectedTitle("");
                }}
              />
              <Button
                title={"Search"}
                size="sm"
                onClick={() => handleIntegratedSearch()}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col mx-2">
          <p className="font-[600] mx-2">Strings History</p>
          <div className="flex flex-1 shadow m-2 p-3 ">
            <NewTab
              tabs={integratedSearchMenus}
              color="gray"
              variant="text"
              activeVariant="text"
            />
          </div>
        </div>
      </div>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  ) : (
    <div className="flex flex-1 flex-col mx-1 h-full overflow-hidden">
      {/* <SearchResults
        action={updatedAction}
        updateStateAction={updateStateAction}
      /> */}
      <IntegratedSearchResults
        togggleSearchApplicant={togggleSearchApplicant}
        iDBQuery={iDBQuery}
        cBQuery={cBQuery}
        monsterQuery={monsterQuery}
      />
    </div>
  );
};

export default IntegratedSearch;